/**
 * @file dateFormatters.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-11
 * @modified 2026-10-08
 * @description Centralized date formatting utilities
 *
 * 1.3.0 (2026-10-08): formatDateDDMMYY (DD/MM/YY). This file is also copied to
 * the server as utils/dateFormatters.js by scripts/sync-shared-utils.js, so the
 * server's messages read the same dates as the screens — edit it here and
 * re-run the script.
 *
 * This module consolidates all date formatting functions previously
 * duplicated across 6+ component files.
 */

/**
 * Format a date string to DD/MM/YYYY
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date or '-' if invalid
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    return '-';
  }
};

/**
 * Format a date as DD/MM/YY (e.g. "01/10/26").
 *
 * A plain 'YYYY-MM-DD' day (how Postgres DATE columns arrive) is read as that
 * calendar day, never through a Date — `new Date('2026-10-01')` is UTC
 * midnight, which a browser west of Greenwich shows as 30 September.
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date or '-' if invalid
 */
export const formatDateDDMMYY = (dateString) => {
  if (!dateString) return '-';
  const m = typeof dateString === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString) : null;
  if (m) return `${m[3]}/${m[2]}/${m[1].slice(2)}`;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(2);
    return `${day}/${month}/${year}`;
  } catch (error) {
    return '-';
  }
};

/**
 * Format a date with ordinal suffix (e.g., "11th November 2025")
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date with ordinal
 */
export const formatDateWithOrdinal = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const day = date.getDate();
    const ordinal = getOrdinalSuffix(day);
    const month = date.toLocaleString('en-GB', { month: 'long' });
    const year = date.getFullYear();

    return `${day}${ordinal} ${month} ${year}`;
  } catch (error) {
    return '';
  }
};

/**
 * Get ordinal suffix for a number (st, nd, rd, th)
 * @param {number} num - Number to get suffix for
 * @returns {string} Ordinal suffix
 */
function getOrdinalSuffix(num) {
  const j = num % 10;
  const k = num % 100;

  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

/**
 * Format a date period (e.g., "1st - 31st January 2025")
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {string} Formatted period
 */
export const formatPeriod = (startDate, endDate) => {
  if (!startDate || !endDate) return '';

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';

    const startDay = start.getDate();
    const endDay = end.getDate();
    const startOrdinal = getOrdinalSuffix(startDay);
    const endOrdinal = getOrdinalSuffix(endDay);

    const startMonth = start.toLocaleString('en-GB', { month: 'long' });
    const endMonth = end.toLocaleString('en-GB', { month: 'long' });
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();

    // Same month and year
    if (startMonth === endMonth && startYear === endYear) {
      return `${startDay}${startOrdinal} - ${endDay}${endOrdinal} ${startMonth} ${startYear}`;
    }

    // Same year, different months
    if (startYear === endYear) {
      return `${startDay}${startOrdinal} ${startMonth} - ${endDay}${endOrdinal} ${endMonth} ${startYear}`;
    }

    // Different years
    return `${startDay}${startOrdinal} ${startMonth} ${startYear} - ${endDay}${endOrdinal} ${endMonth} ${endYear}`;
  } catch (error) {
    return '';
  }
};

/**
 * Format a date to ISO string (YYYY-MM-DD)
 * @param {string|Date} dateString - Date to format
 * @returns {string} ISO formatted date
 */
export const formatDateISO = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    return date.toISOString().split('T')[0];
  } catch (error) {
    return '';
  }
};

/**
 * Format a date for display in tables (short format)
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date (DD MMM YYYY)
 */
export const formatDateShort = (dateString) => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  } catch (error) {
    return '-';
  }
};

/**
 * Format a date with time (DD/MM/YYYY HH:MM)
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date and time
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    return '-';
  }
};

/**
 * Format a time as HH:MM in Europe/London, whatever zone the viewer's machine
 * is set to.
 *
 * Everything the office looks at is on London time, so a screen with a
 * mis-set or non-UK clock should still read the yard's time rather than its
 * own. Used by the live fleet map, where a wrong hour is easy to miss and
 * easy to act on. Requires a full-ICU browser — the TV dashboard is a plain
 * HTML page on a Pi-side Chromium that may not have one, so it converts
 * manually instead.
 *
 * @param {string|Date} dateString - Instant to format (zone-qualified)
 * @returns {string} HH:MM in London time, or '-' if unparseable
 */
export const formatTimeLondon = (dateString) => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/London'
    });
  } catch (error) {
    return '-';
  }
};

/**
 * Format an instant as DD/MM/YYYY HH:MM in Europe/London.
 *
 * The London counterpart to formatDateTime, which renders in the viewer's own
 * zone. Use this for anything describing when the system last did something —
 * a sync, a job, a send — because those happen on the office's clock and a
 * laptop set to another zone would otherwise report the wrong hour.
 *
 * @param {string|Date} dateString - Instant to format (zone-qualified)
 * @returns {string} e.g. "24/08/2026 11:20", or '-' if unparseable
 */
export const formatDateTimeLondon = (dateString) => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/London'
    }).replace(',', '');
  } catch (error) {
    return '-';
  }
};

/**
 * Get relative time string (e.g., "2 days ago", "in 3 hours")
 * @param {string|Date} dateString - Date to compare
 * @returns {string} Relative time string
 */
export const getRelativeTime = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

    return formatDate(date);
  } catch (error) {
    return '';
  }
};
